import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  EnumJobStatus,
  JobStatus,
  JobType,
  skillsUsed,
} from "@utils/constants";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "@lib/dayjs";
import { SkillInput } from "@pages/SearchPage/DetailPage";
import QuillEditorViewOnly from "@components/QuillEditor/QuillEditorViewOnly";
import { timeSincePost } from "@utils/helperFunction";
import { publishUnpublish } from "@endpoints/job.service";
import { setJobDetail } from "@store/actions/job.action";
import { useSnackbar } from "notistack";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import { colors } from "@utils/theme";
import CloseJobModal from "../components/CloseJobModal";
import RefundModal from "../components/RefundModal";
import { ChevronDown } from "lucide-react";
import { RootState } from "@store/reducers";
import PublishIcon from "@assets/svg/PublishIcon";
import { getClassificationSummary } from "@utils/classification";

const tabs = ["Job description", "Skills required"];

const PublishDropdownInput = ({
  jobDetail,
  handlePublishUnpublish,
  publishLoading,
  colors,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const publishOptions = ["Publish", "Unpublish"];

  const currentSelection =
    jobDetail?.status === JobStatus.LIVE ? "Unpublish" : "Publish";

  const handleOptionSelect = (option) => {
    if (option !== currentSelection) {
      handlePublishUnpublish();
    }
    setIsDropdownOpen(false);
  };

  return (
    <div className="relative">
      <div
        className={`flex items-center justify-between rounded-md border px-2 py-1.5 cursor-pointer bg-light-white-700`}
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
      >
        <div className="flex items-center gap-2">
          <PublishIcon className="h-5 w-5" />
          <input
            className="
            bg-transparent 
            text-xs 
            font-medium
            text-charcoal 
            cursor-pointer
            outline-none
          "
            value={currentSelection}
            readOnly
          />
        </div>
        {publishLoading ? (
          <LoadingAnimation color={colors.White} className="w-4 h-4" />
        ) : (
          <ChevronDown fill={"white"} size={15} />
        )}
      </div>
      {isDropdownOpen && (
        <div
          className="
            absolute 
            top-full 
            left-0 
            w-full 
            bg-white 
            rounded-md 
            border 
            border-gray-300 
            z-50 
            shadow-lg 
            max-h-52 
            overflow-y-auto
          "
          onMouseLeave={() => setIsDropdownOpen(false)}
        >
          {publishOptions.map((item, index) => (
            <div
              key={index}
              className={`
                px-4 
                py-2 
                cursor-pointer
                ${
                  item === currentSelection
                    ? "bg-gray-100 font-semibold"
                    : "hover:bg-gray-50"
                }
              `}
              onClick={() => handleOptionSelect(item)}
            >
              <span
                className={`
                text-xs 
                ${
                  item === currentSelection
                    ? "text-charcoal font-semibold"
                    : "text-gray-600"
                }
              `}
              >
                {item}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const RenderBody = ({ activeTab, data }) => {
  switch (activeTab) {
    case 0:
      return (
        <div>
          <QuillEditorViewOnly value={data?.description} />
        </div>
      );

    case 1:
      return (
        <div className="flex flex-col">
          <div className="grid grid-cols-[1.5fr_0.5fr_0.4fr_1fr] w-full gap-2">
            <span className="font-medium text-xs text-charcoal">
              Required Skills
            </span>
            <span className="font-medium text-xs text-charcoal">Rating</span>
            <span className="font-medium text-xs text-charcoal">Years</span>
            <span className="font-medium text-xs text-charcoal">When</span>
            <div />
          </div>
          {data?.skills?.map((skill) => {
            // return <span>asfsadf</span>;
            return (
              <div
                key={skill?._id}
                className="grid grid-cols-[1.5fr_0.5fr_0.4fr_1fr] items-center w-full gap-2 mt-2"
              >
                {/* @ts-ignore */}
                <SkillInput type="skill" value={skill?.title} />
                {/* @ts-ignore */}
                <SkillInput
                  type="rating"
                  placeholder="Rating"
                  value={skill?.rating}
                />
                {/* @ts-ignore */}
                <SkillInput placeholder="Years" value={skill?.years} />
                {/* @ts-ignore */}
                <SkillInput
                  type="skillUsed"
                  placeholder="When last used"
                  value={Object.keys(skillsUsed).find(
                    (key) => skillsUsed[key] === skill?.lastUsed,
                  )}
                />
              </div>
            );
          })}
        </div>
      );

    default:
      break;
  }
};

export const JobPostSection = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { jobDetail } = useSelector((state: RootState) => state?.job);
  const [activeTab, setActiveTab] = useState(0);
  const [publishLoading, setPublishLoading] = useState(false);
  const [openJobCloseModal, setOpenJobCloseModal] = useState(false);
  const [openRefundModal, setOpenRefundModal] = useState(false);

  const job =
    JobType.find(({ value }) => value === jobDetail?.jobType)?.label || "-";

  const detailsColumn = [
    {
      title: "Location",
      value: jobDetail?.location,
    },
    {
      title: "Industry Type",
      value: jobDetail?.industryType,
    },
    {
      title: "Classification",
      value: getClassificationSummary(jobDetail?.classification),
    },
    {
      title: "Job Type",
      value: JobType?.find((item) => item?.value === jobDetail?.jobType)?.label,
    },
    {
      title: "Salary Range",
      value: `${jobDetail?.currency + jobDetail?.salary?.min} - ${
        jobDetail?.currency + jobDetail?.salary?.max
      }`,
    },
  ];

  const publishedColumn = [
    {
      title: "Views",
      value: "-",
    },
    {
      title: "Applicants",
      value: jobDetail?.applicantData?.length || 0,
    },
  ];

  const publishedColumn2 = [
    {
      title: "Published",
      value: timeSincePost(jobDetail?.publishedDate) || "N/A",
    },
    {
      title: "Expires",
      value: dayjs(jobDetail?.expiredAt)?.format("Do MMM YY hh:mm a"),
    },
    {
      title: "Edited",
      value: dayjs(jobDetail?.updatedAt)?.format("Do MMM YY hh:mm a"),
    },
  ];

  const handlePublishUnpublish = async () => {
    try {
      setPublishLoading(true);
      const jobStatus = jobDetail?.status === JobStatus.LIVE ? 2 : 1;
      const response = await publishUnpublish(jobDetail?._id, jobStatus);
      const { success, data } = response;
      if (success) {
        dispatch(
          setJobDetail({ ...jobDetail, status: jobStatus === 2 ? 5 : 2 }),
        );
        enqueueSnackbar(data?.message, {
          variant: "success",
        });
      }
    } catch (error) {
      enqueueSnackbar("An error occurred", {
        variant: "error",
      });
    } finally {
      setPublishLoading(false);
    }
  };

  console.log("This is job details", jobDetail);

  return (
    <section className="grid grid-cols-[1.3fr_0.7fr] pt-6 relative gap-6 min-h-0 flex-1">
      {/* Left section */}
      <div className="flex flex-col rounded-md py-6 px-5 border border-[#F1F5F9] w-full flex-1 overflow-y-scroll no-scrollbar">
        <div className="flex items-center justify-between">
          <label className="font-semibold text-charcoal">
            {jobDetail?.title}
          </label>
          <span className="font-medium text-charcoal text-xs">
            {jobDetail?.refNo}
          </span>
        </div>
        <div className="flex flex-col gap-3.5 py-4">
          {detailsColumn?.map((item) => {
            return (
              <div key={item?.title} className="flex items-center">
                <span className="font-medium text-xs text-light-black w-28">
                  {item?.title}:
                </span>
                <span className="font-semibold text-charcoal text-xs">
                  {item?.value}
                </span>
              </div>
            );
          })}
        </div>
        {/* Tabs section */}
        <div className="flex flex-col mt-10 gap-5">
          <div className="flex items-center gap-8 border-b border-gray-200">
            {tabs?.map((tab, tabIndex) => {
              const isActive = activeTab === tabIndex;
              return (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tabIndex)}
                  className={`text-xs font-semibold border-b-2 px-1 pb-1 ${
                    isActive
                      ? "border-primary text-charcoal"
                      : "border-transparent text-light-grey"
                  }`}
                >
                  {tab}
                </button>
              );
            })}
          </div>
          <RenderBody activeTab={activeTab} data={jobDetail} />
        </div>
      </div>
      {/* Right section */}
      <div className="bg-[#fafafa] p-4 rounded-md border h-max">
        <div className="flex flex-col gap-7 rounded-md bg-white py-6 px-3">
          {[JobStatus.LIVE, JobStatus.DRAFT, JobStatus.UNPUBLISHED]?.includes(
            jobDetail?.status,
          ) ? (
            <PublishDropdownInput
              jobDetail={jobDetail}
              handlePublishUnpublish={handlePublishUnpublish}
              publishLoading={publishLoading}
              colors={colors}
            />
          ) : null}
          <div className="flex flex-col gap-2">
            {publishedColumn?.map((item) => {
              return (
                <div key={item?.title} className="flex items-center">
                  <span className="text-xs text-light-black w-36">
                    {item?.title}:
                  </span>
                  <span className="font-medium text-charcoal text-xs">
                    {item?.value}
                  </span>
                </div>
              );
            })}
          </div>

          <div className="flex flex-col gap-2 border-b border-gray-300 pb-6">
            {publishedColumn2?.map((item) => {
              return (
                <div key={item?.title} className="flex items-center">
                  <span className="font-medium text-xs text-light-black w-36">
                    {item?.title}:
                  </span>
                  <span className="font-medium text-charcoal text-xs">
                    {item?.value}
                  </span>
                </div>
              );
            })}
          </div>
          {/* Action button section */}
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={() => navigate("/job/create", { state: jobDetail })}
              className="group flex-1 flex items-center justify-center font-semibold text-xs bg-[#F1F5F9] rounded-md py-1.5 border hover:bg-gray-200"
            >
              <span>Edit job post</span>
            </button>
            {jobDetail?.status === EnumJobStatus.CLOSED &&
            !jobDetail?.isRefunded ? (
              <button
                onClick={() =>
                  navigate("/referral/refunds/refund-summary", {
                    state: jobDetail,
                  })
                }
                // onClick={() => navigate("/job/create", { state: jobDetail })}
                className="group flex-1 flex items-center justify-center font-semibold text-white text-xs bg-primary rounded-md py-1.5 border hover:bg-black"
              >
                <span>Refund Now</span>
              </button>
            ) : null}
            {jobDetail?.status === EnumJobStatus.LIVE ? (
              <button
                onClick={() => setOpenJobCloseModal(true)}
                className="group flex-1 flex items-center justify-center font-semibold text-white text-xs bg-light-black rounded-md py-1.5 border hover:bg-black"
              >
                <span>Close job post</span>
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <CloseJobModal
        openModal={openJobCloseModal}
        setOpenModal={setOpenJobCloseModal}
        onConfirm={() => setOpenRefundModal(true)}
      />

      <RefundModal
        openModal={openRefundModal}
        setOpenModal={setOpenRefundModal}
      />
    </section>
  );
};
